"use client";

import { useContentReaction } from "@/hooks/useContentReaction";
import { cn } from "@/lib/utils";
import { useContentDetailModal } from "hooks/useContentDetailModal";
import { HeartIcon, MessageCircleIcon } from "lucide-react";
import { ComponentProps, useCallback } from "react";

import { ContentThumbnailImg } from "./ContentThumbnailImg";

interface ContentThumbnailWithActionsProps extends Omit<
    ComponentProps<typeof ContentThumbnailImg>,
    "src" | "content"
> {
    content: Pick<
        Mix_ContentFragment,
        "id" | "originalurl" | "reacted" | "reaction_count" | "comment_count"
    > & { imgurl?: string | null };
    /** 콘텐츠 목록 (네비게이션용) */
    contentList?: Mix_ContentFragment[];
    /** 액션 버튼 표시 여부 */
    showActions?: boolean;
}

// 액션 버튼 공통 스타일
const ActionButton = ({
    onClick,
    isActive,
    activeColor,
    children,
    className
}: {
    onClick: (e: React.MouseEvent) => void;
    isActive?: boolean;
    activeColor?: string;
    children: React.ReactNode;
    className?: string;
}) => (
    <button
        onClick={onClick}
        className={cn(
            `flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-xs
            text-white backdrop-blur-sm transition-all duration-200
            hover:bg-black/80`,
            isActive && activeColor,
            className
        )}
    >
        {children}
    </button>
);

export const ContentThumbnailWithActions = ({
    content,
    contentList,
    showActions = true,
    className,
    ...props
}: ContentThumbnailWithActionsProps) => {
    const { openContentModal } = useContentDetailModal();
    const {
        toggle: toggleReaction,
        isReacted,
        reactionCount
    } = useContentReaction(content);

    // 좋아요 버튼 클릭 핸들러
    const handleReactionClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            toggleReaction();
        },
        [toggleReaction]
    );

    // 댓글 버튼 클릭 핸들러 - 모달을 열고 댓글 섹션으로 스크롤
    const handleCommentClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            // 모달 열기
            openContentModal(content.id, contentList);
            // 댓글 섹션으로 스크롤 (모달이 열린 후 약간의 지연 필요)
            setTimeout(() => {
                const commentSection = document.querySelector(
                    "[data-comment-section]"
                );
                commentSection?.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });
            }, 300);
        },
        [content.id, contentList, openContentModal]
    );

    return (
        <div className="relative h-full w-full">
            <ContentThumbnailImg
                src={content.imgurl ?? ""}
                className={className}
                {...props}
            />

            {/* 액션 버튼 오버레이 (오른쪽 하단) */}
            {showActions && (
                <div
                    className="absolute bottom-2 right-2 z-10 flex items-center
                        gap-1.5"
                >
                    {/* 댓글 버튼 */}
                    <ActionButton onClick={handleCommentClick}>
                        <MessageCircleIcon className="size-3.5" />
                        <span>{content.comment_count ?? 0}</span>
                    </ActionButton>
                    {/* 좋아요 버튼 */}
                    <ActionButton
                        onClick={handleReactionClick}
                        isActive={isReacted}
                        activeColor="text-[#FF6B6B]"
                    >
                        <HeartIcon
                            className={cn(
                                "size-3.5",
                                isReacted && "fill-current text-[#FF6B6B]"
                            )}
                        />
                        <span>{reactionCount}</span>
                    </ActionButton>
                </div>
            )}
        </div>
    );
};
